// Weekly programmations (« Mes programmations », #/programmations). DEMO fixtures, not a production capability: several published
// weeks (28 septembre → 25 octobre), one week without any programmation, two programmations offered AU CHOIX in the current week.
// The current « Force & mobilité » week REUSES the canonical home PROGRAMME / Séance A (seance-fixtures SEANCE, store.seance) and
// the Bibliothèque sessions B and C; past weeks read the realised sessions already in Mon suivi (SESSIONS) by title and dates.
// Other sessions keep their own session-local state here (keyed by session id), so the canonical Séance A, its logs and the
// realised sessions are never overwritten. Nothing is sent.
import { useSyncExternalStore } from "react";
import { PROGRAMME } from "./home-fixtures";
import { PROGRAMMES } from "./bibliotheque-fixtures";
import { SESSIONS, type Session } from "./suivi-fixtures";
import { SEANCE, buildSession, type SessionDef } from "./seance-fixtures";
import { isoAdd, store, type SeanceState } from "./store";
import type { CoachId } from "./messagerie-state";
import { DEMO_MONDAY } from "./demo-context";

export const CURRENT_WEEK = DEMO_MONDAY;
export const WEEKS = ["2026-09-28", "2026-10-05", "2026-10-12", "2026-10-19", "2026-10-26"];
export const CANONICAL_ID = "s3-A";

/** `date`: DEMO assignment of a session to a day (mock data lot 13/10); absent = no day imposed. */
export interface ProgSession { id: string; title: string; minutes: number; exercises?: readonly string[]; date?: string }
export interface Programmation {
  id: string; name: string; week: string; coach: CoachId; theme: string; sessions: ProgSession[];
  /** Demo instructions shown in « Consignes du coach »; absent → block hidden. */
  notes?: readonly string[];
  /** Programmations sharing a group are offered AU CHOIX: only the chosen one counts. */
  choice?: string;
}

const fm = PROGRAMMES[0];
// Current week only: A on Monday 12 (done this morning), B on Wednesday 14, C on Friday 16 (DEMO assignment).
const S3_DATES: Record<string, string> = { A: "2026-10-12", B: "2026-10-14", C: "2026-10-16" };
const fmSessions = (prefix: string): ProgSession[] => (fm?.sessions ?? []).map(s => ({
  id: prefix === "s3" && s.letter === "A" ? CANONICAL_ID : `${prefix}-${s.letter}`,
  title: s.letter === "A" && PROGRAMME ? PROGRAMME.session : `Séance ${s.letter} · ${s.title}`, minutes: s.minutes, exercises: s.exercises,
  ...(prefix === "s3" && S3_DATES[s.letter] ? { date: S3_DATES[s.letter] } : {}),
}));
const fmProg = (week: string, prefix: string): Programmation => ({ id: `fm-${prefix}`, name: fm?.title ?? "Force & mobilité", week, coach: "alexandre",
  theme: "Force et mobilité", sessions: fmSessions(prefix), notes: fm?.coachNotes });
// Mobility exercises: the steps of the Bibliothèque resource « Mobilité des hanches » (r1), shortened to their movement names.
const MOBILITY = ["Rotations de hanches debout", "Fente basse avec bascule du bassin", "Squat profond tenu", "Pont fessier lent"];

export const PROGRAMMATIONS: Programmation[] = fm ? [
  fmProg("2026-09-28", "s1"), fmProg("2026-10-05", "s2"), fmProg("2026-10-12", "s3"),
  // Mock data lot 13/10: the current week holds Force & mobilité only (1/3 clear). « Cardio doux » had no exercise content and
  // is removed (the launch guard stays in code); the optional mobility programmation is next week's, without imposed day.
  fmProg("2026-10-19", "s4"),
  { id: "mob-s4", name: "Mobilité au quotidien", week: "2026-10-19", coach: "antoine", theme: "Mobilité",
    sessions: [{ id: "s4-mob", title: "Mobilité · hanches et dos", minutes: 20, exercises: MOBILITY }],
    notes: ["À faire un jour sans séance, sans chercher l'amplitude maximale."] },
  // 26 octobre: nothing published yet (empty week).
] : [];

export const weekOfProg = (week: string) => PROGRAMMATIONS.filter(p => p.week === week);
export const progById = (id: string) => PROGRAMMATIONS.find(p => p.id === id);
export const findSession = (sid: string) => { for (const p of PROGRAMMATIONS) { const s = p.sessions.find(x => x.id === sid); if (s) return { p, s }; } return undefined; };
export const weekLabel = (week: string) => {
  const a = new Date(`${week}T12:00:00`), b = new Date(`${isoAdd(week, 6)}T12:00:00`);
  const m = (d: Date) => d.toLocaleDateString("fr-FR", { month: "long" });
  return a.getMonth() === b.getMonth() ? `${a.getDate()}–${b.getDate()} ${m(b)}` : `${a.getDate()} ${m(a)} – ${b.getDate()} ${m(b)}`;
};

/** Definition played by the Séance page: the canonical SEANCE for Séance A of the current week, otherwise built the same way. */
export function sessionDef(sid: string): SessionDef | null {
  if (sid === CANONICAL_ID) return SEANCE;
  const f = findSession(sid);
  return f ? buildSession({ id: sid, programme: f.p.name, title: f.s.title, minutes: f.s.minutes, exercises: f.s.exercises }) : null;
}

/* ---------- Session-local state of the other sessions + choices ---------- */

const fresh = (): SeanceState => ({ started: false, current: 0, logs: {}, done: false, feedback: { comment: "", sent: false } });
interface State { sessions: Record<string, SeanceState>; choice: Record<string, string> }
let state: State = { sessions: {}, choice: {} };
const listeners = new Set<() => void>();
const set = (next: State) => { state = next; listeners.forEach(l => l()); };
export const progState = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  session: (sid: string) => state.sessions[sid] ?? fresh(),
  setSession: (sid: string, patch: Partial<SeanceState>) => set({ ...state, sessions: { ...state.sessions, [sid]: { ...(state.sessions[sid] ?? fresh()), ...patch } } }),
  choose: (group: string, progId: string) => set({ ...state, choice: { ...state.choice, [group]: progId } }),
};
export const useProgState = () => useSyncExternalStore(progState.subscribe, progState.get, progState.get);
/** The programmation counted in an « au choix » group: the member's choice, else the first offered. */
export const chosenIn = (group: string) => state.choice[group] ?? PROGRAMMATIONS.find(p => p.choice === group)?.id;
/** Programmations that count this week: all, but only the chosen one of each « au choix » group. */
export const countedProgs = (week: string) => weekOfProg(week).filter(p => !p.choice || chosenIn(p.choice) === p.id);

/* ---------- Status of a session (realised sessions only) ---------- */

const inWeek = (date: string, week: string) => date >= week && date <= isoAdd(week, 6);
/** The realised session recorded for this programmation session, if any (new records first, then Mon suivi history by title). */
export function realised(p: Programmation, s: ProgSession): Session | undefined {
  const all = [...store.get().doneSessions, ...SESSIONS];
  return all.find(x => x.programmeSessionId === s.id)
    ?? (s.id === CANONICAL_ID ? store.get().doneSessions.find(x => x.id === "seance-s3-A") : undefined)
    ?? all.find(x => x.kind === "programme" && !x.programmeSessionId && x.id !== "seance-s3-A" && x.title === s.title && inWeek(x.date, p.week));
}
export type SessionStatus = "todo" | "started" | "done";
export function statusOf(p: Programmation, s: ProgSession): SessionStatus {
  if (realised(p, s)) return "done";
  const st = s.id === CANONICAL_ID ? store.get().seance : progState.session(s.id);
  return st.done ? "done" : st.started ? "started" : "todo";
}
